/**
 * Message List - WhatsApp-style bubble layout
 * Stream-style ID-based pagination: load older on scroll to top (id_lt / before), cursors in response.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { List, Spin, Empty, message as antMessage, Modal, Input } from 'antd';
import { useQuery, useQueryClient } from 'react-query';
import api from '../../config/api';
import { useChatWebSocket } from '../../contexts/ChatWebSocketContext';
import { useAuth } from '../../contexts/AuthContext';
import { MessageItem } from './MessageItem';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import './MessageList.css';

dayjs.extend(relativeTime);

const MESSAGE_PAGE_SIZE = 50;
const LOAD_OLDER_THRESHOLD_PX = 120;

interface Message {
  id: string;
  channelId: string;
  userId: string;
  text: string;
  contentType: string;
  attachmentUrl?: string;
  attachmentName?: string;
  threadCount: number;
  createdAt: string;
  updatedAt?: string;
  isPinned?: boolean;
  isEdited?: boolean;
  isDeleted?: boolean;
  isSystem?: boolean;
  systemType?: string;
  systemData?: Record<string, unknown>;
  reactions?: Array<{
    emoji: string;
    count: number;
    userIds: string[];
    hasUserReaction?: boolean;
  }>;
  user: { id: string; firstName: string; lastName: string; email: string };
}

interface MessageListProps {
  channelId: string;
  onReplyToMessage?: (messageId: string) => void;
  onOpenThread?: (messageId: string) => void;
}

export const MessageList: React.FC<MessageListProps> = ({
  channelId,
  onReplyToMessage,
  onOpenThread,
}) => {
  const { on: socketOn, presence } = useChatWebSocket();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState<Message[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMoreOlder, setHasMoreOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [shouldScroll, setShouldScroll] = useState(false);

  useEffect(() => {
    (window as any).scrollChatToBottom = () => {
      const el = (window as any).chatMessageListContainer || document.querySelector('.message-list-container');
      if (el) setTimeout(() => { el.scrollTop = el.scrollHeight; }, 100);
    };
    return () => { delete (window as any).scrollChatToBottom; };
  }, []);

  const { data: messagesData } = useQuery(
    ['chat-messages', channelId],
    () =>
      api
        .get(`/api/chat/channels/${channelId}/messages`, { params: { limit: MESSAGE_PAGE_SIZE } })
        .then(res => res.data),
    { enabled: !!channelId, refetchOnWindowFocus: false }
  );

  useEffect(() => {
    if (channelId) {
      api.post(`/api/chat/channels/${channelId}/read`).catch(() => {});
    }
  }, [channelId]);

  useEffect(() => {
    if (!messagesData?.messages || !channelId) return;
    setNextCursor(messagesData.next_cursor ?? null);
    setHasMoreOlder(!!messagesData.has_more_older);
    const loadReactions = async () => {
      const withReactions = await Promise.all(
        (messagesData.messages as Message[]).map(async (msg: Message) => {
          try {
            const r = await api.get(`/api/chat/channels/${channelId}/messages/${msg.id}/reactions`);
            return { ...msg, reactions: r.data.reactions || [] };
          } catch {
            return { ...msg, reactions: [] };
          }
        })
      );
      setMessages(withReactions);
    };
    loadReactions();
  }, [messagesData, channelId]);

  useEffect(() => {
    if (channelId && messagesData?.messages?.length) {
      const el = document.querySelector('.message-list-container');
      if (el) setTimeout(() => { el.scrollTop = el.scrollHeight; }, 300);
    }
  }, [channelId]);

  const loadOlderMessages = useCallback(async () => {
    if (!channelId || !nextCursor || loadingOlder || !hasMoreOlder) return;
    const el = document.querySelector('.message-list-container');
    const prevScrollHeight = el ? el.scrollHeight : 0;
    const prevScrollTop = el ? el.scrollTop : 0;

    setLoadingOlder(true);
    try {
      const res = await api.get(`/api/chat/channels/${channelId}/messages`, {
        params: { limit: MESSAGE_PAGE_SIZE, before: nextCursor },
      });
      const data = res.data;
      const older = (data.messages || []) as Message[];
      if (older.length === 0) {
        setHasMoreOlder(false);
        setLoadingOlder(false);
        return;
      }
      const withReactions = await Promise.all(
        older.map(async (msg: Message) => {
          try {
            const r = await api.get(`/api/chat/channels/${channelId}/messages/${msg.id}/reactions`);
            return { ...msg, reactions: r.data.reactions || [] };
          } catch {
            return { ...msg, reactions: [] };
          }
        })
      );
      setMessages(prev => [...withReactions, ...prev]);
      setNextCursor(data.next_cursor ?? null);
      setHasMoreOlder(!!data.has_more_older);

      requestAnimationFrame(() => {
        const container = document.querySelector('.message-list-container');
        if (container) {
          const newScrollHeight = container.scrollHeight;
          container.scrollTop = newScrollHeight - (prevScrollHeight - prevScrollTop);
        }
      });
    } catch {
      antMessage.error('Failed to load older messages');
    } finally {
      setLoadingOlder(false);
    }
  }, [channelId, nextCursor, loadingOlder, hasMoreOlder]);

  useEffect(() => {
    const el = document.querySelector('.message-list-container');
    if (!el) return;
    const onScroll = () => {
      if (el.scrollTop < LOAD_OLDER_THRESHOLD_PX && hasMoreOlder && !loadingOlder) {
        loadOlderMessages();
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [hasMoreOlder, loadingOlder, loadOlderMessages]);

  useEffect(() => {
    if (!channelId) return;
    const unsub = socketOn('message:new', (newMsg: Message & { isDeleted?: boolean; deleted?: boolean }) => {
      if (newMsg.channelId !== channelId) return;
      if (newMsg.isDeleted || newMsg.deleted) {
        setMessages(prev => prev.map(m => (m.id === newMsg.id ? { ...m, isDeleted: true, text: '' } : m)));
        return;
      }
      setMessages(prev => (prev.some(m => m.id === newMsg.id) ? prev : [...prev, newMsg]));
      setShouldScroll(true);
    });
    return unsub;
  }, [channelId, socketOn]);

  const handleDeleteMessage = async (messageId: string) => {
    if (!isAdmin) return;
    try {
      await api.delete(`/api/chat/channels/${channelId}/messages/${messageId}`);
      antMessage.success('Message deleted');
      setMessages(prev => prev.map(m => (m.id === messageId ? { ...m, isDeleted: true, text: '' } : m)));
    } catch {
      antMessage.error('Failed to delete message');
    }
  };

  useEffect(() => {
    if (shouldScroll && messages.length > 0) {
      setTimeout(() => {
        const el = document.querySelector('.message-list-container');
        if (el) {
          el.scrollTop = el.scrollHeight;
          setShouldScroll(false);
        }
      }, 150);
    }
  }, [shouldScroll, messages]);

  const formatDateTime = (ts: string) => {
    const d = dayjs(ts);
    if (d.isToday()) return `Today at ${d.format('h:mm A')}`;
    if (d.isYesterday()) return `Yesterday at ${d.format('h:mm A')}`;
    return d.format('MMMM D, YYYY [at] h:mm A');
  };

  const shouldShowDateSeparator = (curr: Message, prev: Message | null) => {
    if (!prev) return true;
    return !dayjs(curr.createdAt).isSame(prev.createdAt, 'day');
  };

  const isSameUser = (curr: Message, prev: Message | null) => {
    if (!prev) return false;
    return curr.userId === prev.userId && dayjs(curr.createdAt).diff(dayjs(prev.createdAt), 'minute') < 5;
  };

  // Simple read heuristic: for DMs, consider messages "read" when we've been in the channel
  const isMessageRead = (_msg: Message) => false;

  if (!messagesData && channelId) {
    return (
      <div className="message-list-loading">
        <Spin size="large" />
      </div>
    );
  }

  if (messages.length === 0 && channelId) {
    return (
      <div className="message-list-empty whatsapp-empty">
        <Empty description="No messages yet. Say hello!" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </div>
    );
  }

  return (
    <div className="message-list whatsapp-message-list">
      {hasMoreOlder && (
        <div className="message-list-load-older">
          {loadingOlder ? (
            <Spin size="small" />
          ) : (
            <button type="button" className="load-older-btn" onClick={loadOlderMessages}>
              Load older messages
            </button>
          )}
        </div>
      )}
      <List
        dataSource={messages}
        renderItem={(message, index) => {
          const prev = index > 0 ? messages[index - 1] : null;
          const showAvatar = !isSameUser(message, prev);
          const showDateSep = shouldShowDateSeparator(message, prev);
          const userPresence = presence[message.userId];

          if (message.isSystem) {
            return (
              <>
                {showDateSep && (
                  <List.Item className="date-separator" style={{ border: 'none', padding: '8px 16px' }}>
                    <div className="date-separator-watermark">{formatDateTime(message.createdAt)}</div>
                  </List.Item>
                )}
                <List.Item className="system-message" style={{ border: 'none', padding: '8px 16px' }}>
                  <div className="system-message-watermark">
                    {message.systemType === 'join' &&
                      `👋 ${message.user.firstName} ${message.user.lastName} joined`}
                    {message.systemType === 'leave' &&
                      `👋 ${message.user.firstName} ${message.user.lastName} left`}
                  </div>
                </List.Item>
              </>
            );
          }

          return (
            <>
              {showDateSep && (
                <List.Item className="date-separator" style={{ border: 'none', padding: '8px 16px' }}>
                  <div className="date-separator-watermark">{formatDateTime(message.createdAt)}</div>
                </List.Item>
              )}
              <List.Item style={{ border: 'none', padding: 0 }}>
                <MessageItem
                  message={message}
                  showAvatar={showAvatar}
                  userPresence={userPresence}
                  isRead={isMessageRead(message)}
                  canDeleteMessage={isAdmin}
                  onReply={onReplyToMessage ? () => onReplyToMessage(message.id) : undefined}
                  onEdit={async (msgId, txt) => {
                    const m = messages.find(x => x.id === msgId);
                    if (!m) return;
                    const mins = (Date.now() - new Date(m.createdAt).getTime()) / 60000;
                    if (mins > 10) {
                      antMessage.error('Messages can only be edited within 10 minutes');
                      return;
                    }
                    setEditingMessageId(msgId);
                    setEditText(txt);
                  }}
                  onDelete={isAdmin ? () => handleDeleteMessage(message.id) : undefined}
                  onReaction={async (msgId, emoji) => {
                    try {
                      await api.post(`/api/chat/channels/${channelId}/messages/${msgId}/reactions`, { emoji });
                      const r = await api.get(`/api/chat/channels/${channelId}/messages/${msgId}/reactions`);
                      setMessages(prev =>
                        prev.map(m => (m.id === msgId ? { ...m, reactions: r.data.reactions } : m))
                      );
                    } catch {
                      antMessage.error('Failed to add reaction');
                    }
                  }}
                />
              </List.Item>
            </>
          );
        }}
      />
      <Modal
        title="Edit message"
        open={!!editingMessageId}
        onOk={async () => {
          if (!editText.trim()) return;
          try {
            await api.put(`/api/chat/channels/${channelId}/messages/${editingMessageId}`, {
              text: editText,
            });
            antMessage.success('Message updated');
            setEditingMessageId(null);
            setEditText('');
            queryClient.invalidateQueries(['chat-messages', channelId]);
          } catch {
            antMessage.error('Failed to edit');
          }
        }}
        onCancel={() => {
          setEditingMessageId(null);
          setEditText('');
        }}
        okText="Save"
        cancelText="Cancel"
      >
        <Input.TextArea
          value={editText}
          onChange={e => setEditText(e.target.value)}
          autoSize={{ minRows: 3, maxRows: 10 }}
        />
      </Modal>
    </div>
  );
};
